import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { buildLeaderboardData, type LeaderboardMetric, type LeaderboardTimeRange } from './leaderboardUtils'
import { formatWeekRange, getCurrentWeekKey } from './usageDashboardUtils'

import styles from './styles.module.scss'

import type { EnterpriseUserUsage } from '@/api/usage'
import Button from '@/components/Button'
import { usePricing } from '@/hooks/usePricing'
import { c } from '@/utils'

const MEDAL_EMOJI: Record<number, string> = {
  1: '🥇',
  2: '🥈',
  3: '🥉',
}

// Deterministic avatar colors based on user_id hash
const AVATAR_COLORS = ['#7c3aed', '#8b4755', '#2563eb', '#059669', '#dc2626', '#f59e0b', '#8b5cf6', '#06b6d4']

function getAvatarColor(userId: string): string {
  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) | 0
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function formatStreakValue(days: number): string {
  if (days === 0) return '0 days'
  if (days === 1) return '1 day'
  return `${days} days`
}

interface LeaderboardSectionProps {
  members: EnterpriseUserUsage[]
  currentUserId: string
  canViewLeaderboard: boolean
  canViewTeammateMetrics: boolean
}

export default function LeaderboardSection({
  members,
  currentUserId,
  canViewLeaderboard,
  canViewTeammateMetrics,
}: LeaderboardSectionProps) {
  const [metric, setMetric] = useState<LeaderboardMetric>('words')
  const [timeRange, setTimeRange] = useState<LeaderboardTimeRange>('thisWeek')
  const navigate = useNavigate()
  const { locale, localizeNumber } = usePricing()

  const leaderboard = useMemo(
    () => buildLeaderboardData(members, metric, timeRange, currentUserId),
    [members, metric, timeRange, currentUserId]
  )

  const weekDateRange = useMemo(() => formatWeekRange(getCurrentWeekKey(), locale), [locale])

  const upgradeBannerMessage = !canViewLeaderboard
    ? 'Upgrade to Enterprise plan to view individual teammate metrics'
    : ''

  const isStreak = metric === 'streak'
  const valueColumnHeader = isStreak ? 'STREAK' : 'WORDS'

  return (
    <div style={{ position: 'relative' }}>
      <div className={styles.leaderboardHeader}>
        <div className={styles.leaderboardControls}>
          <div className={styles.viewToggle}>
            <button
              className={c(
                styles.toggleButton,
                !isStreak && timeRange === 'thisWeek' ? styles.active : '',
                isStreak ? styles.disabled : ''
              )}
              onClick={() => !isStreak && setTimeRange('thisWeek')}
              aria-disabled={isStreak}
              title={!isStreak ? weekDateRange : undefined}
            >
              This Week
            </button>
            <button
              className={c(
                styles.toggleButton,
                isStreak || timeRange === 'allTime' ? styles.active : ''
              )}
              onClick={() => !isStreak && setTimeRange('allTime')}
            >
              All Time
            </button>
          </div>
          <select
            className={styles.metricSelect}
            value={metric}
            onChange={(e) => setMetric(e.target.value as LeaderboardMetric)}
          >
            <option value="words">Words</option>
            <option value="streak">Streak</option>
          </select>
        </div>
      </div>

      <div className={styles.tableCard} style={{ position: 'relative' }}>
        {upgradeBannerMessage && (
          <>
            <div className={styles.sectionBanner}>
              <div>{upgradeBannerMessage}</div>
              <Button
                title="Upgrade to Enterprise"
                onClick={() => navigate('/app/settings/billing')}
                type="dark"
              />
            </div>
            <div className={styles.sectionBlurOverlay} />
          </>
        )}
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: 56 }}>RANK</th>
              <th>NAME</th>
              <th style={{ textAlign: 'right' }}>{valueColumnHeader}</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.length === 0 && (
              <tr>
                <td colSpan={3} className={styles.loadingState}>
                  No usage data yet
                </td>
              </tr>
            )}
            {leaderboard.map((entry) => {
              // Don't show medals when everyone has zero activity
              const hasActivity = leaderboard.length > 0 && leaderboard[0].value > 0
              const medal = hasActivity ? MEDAL_EMOJI[entry.rank] : undefined
              const isTop3 = hasActivity && entry.rank <= 3
              const showBlurredValue = !canViewTeammateMetrics && !entry.isYou

              let medalRowClass = ''
              if (isTop3 && entry.rank === 1) medalRowClass = styles.medalRowGold
              else if (isTop3 && entry.rank === 2) medalRowClass = styles.medalRowSilver
              else if (isTop3 && entry.rank === 3) medalRowClass = styles.medalRowBronze

              const formattedValue =
                metric === 'streak' ? formatStreakValue(entry.value) : localizeNumber(entry.value)

              return (
                <tr key={entry.userId} className={medalRowClass}>
                  <td>
                    <div className={styles.rankCell}>
                      {medal ? (
                        <span className={styles.medalEmoji}>{medal}</span>
                      ) : (
                        <span className={styles.medalPlaceholder} />
                      )}
                      <span className={styles.rankNumber}>{entry.rank}</span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.memberInfo}>
                      <div className={styles.avatar} style={{ backgroundColor: getAvatarColor(entry.userId) }}>
                        {entry.avatar}
                      </div>
                      <div className={styles.memberDetails}>
                        <div className={styles.memberName}>
                          {entry.name}
                          {entry.isYou && <span className={styles.youBadge}>YOU</span>}
                        </div>
                        <div className={styles.memberEmail}>{entry.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className={c(styles.wordCount, isTop3 && styles.topWordCount)}>
                    {showBlurredValue ? (
                      <span className={styles.blurredWordCount}>
                        {/* Random number for privacy */}
                        {localizeNumber(Math.abs(entry.userId.split('').reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) | 0, 0) % 100))}
                      </span>
                    ) : (
                      formattedValue
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
